import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from 'react-dom';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_CONFIG } from '../../config/apiConfig';
import ic1 from '../../images/ic1.png';
import ic2 from '../../images/ic2.png';
import ic3 from '../../images/ic3.png';
import arrowDownIcon from '../../images/ic4.png';
import './index.css';

// Portal 组件用于将下拉菜单渲染到 body，并计算正确位置
function DropdownPortal({ children, isOpen, filterRef }) {
    const [position, setPosition] = useState({ top: 0, left: 0 });
    
    useEffect(() => {
        if (isOpen && filterRef?.current) {
            const updatePosition = () => {
                const rect = filterRef.current.getBoundingClientRect();
                
                // 计算下拉菜单的合适位置
                let left = rect.left;
                let top = rect.bottom;
                
                // 确保下拉菜单不会超出屏幕右边界
                const dropdownWidth = 180; // 调整宽度以适应筛选框大小
                const screenWidth = window.innerWidth;
                if (left + dropdownWidth > screenWidth - 10) {
                    left = screenWidth - dropdownWidth - 10;
                }
                
                // 确保下拉菜单不会超出屏幕底部
                // 移除了固定高度限制，让内容自然展开
                const screenHeight = window.innerHeight;
                // 简单检查底部空间，如果不足则向上显示
                if (top + 100 > screenHeight - 10) { // 100px为预估的最小高度
                    top = rect.top - 100 - 5; // 向上显示
                }
                
                setPosition({
                    top,
                    left
                });
            };
            updatePosition();
            
            // 监听滚动和窗口大小变化
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
            
            return () => {
                window.removeEventListener('scroll', updatePosition, true);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [isOpen, filterRef]);

    if (!isOpen) return null;
    
    return ReactDOM.createPortal(
        <div
            className="filter-dropdown"
            style={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                zIndex: 99
            }}
        >
            {children}
        </div>,
        document.body
    );
}

export default function VehicleSelect() {
    const navigate = useNavigate();
    
    // 状态管理
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [vehicleUsageTimes, setVehicleUsageTimes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [vehicleGroups, setVehicleGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [availableSeats, setAvailableSeats] = useState([{ seatName: '全部' }]);
    const [selectedSeats, setSelectedSeats] = useState('全部');
    // 预约详情弹窗
    const [showReservationDetail, setShowReservationDetail] = useState(false);
    const [selectedReservation, setSelectedReservation] = useState(null);
    // 用于跟踪当前悬停/触摸的预约时间段
    const [hoveredReservationId, setHoveredReservationId] = useState(null);
    const [hoveredReservationData, setHoveredReservationData] = useState(null);
    // 用于跟踪弹窗位置
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
    // 下拉框显示状态管理
    const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);
    const [isSeatsDropdownOpen, setIsSeatsDropdownOpen] = useState(false);
    // 使用ref追踪筛选容器和筛选框
    const filterContainerRef = useRef(null);
    const groupFilterRef = useRef(null);
    const seatsFilterRef = useRef(null);
    
    // 生成未来7天的日期
    function generateNext7Days() {
        const days = [];
        const today = new Date();
        
        for (let i = 0; i < 7; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const weekDay = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][date.getDay()];
            
            days.push({
                date: `${year}-${month}-${day}`,
                day: day,
                weekDay: weekDay
            });
        }
        
        return days;
    }
    
    // 格式化时间为24小时制
    function formatTime(time) {
        return time.substring(5, 16);
    }

    const bindReservationDetailEvents = (timeSlot, reservationId) => ({
        onMouseEnter: (e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setPopupPosition({
                top: rect.top - 120,
                left: rect.left + (rect.width / 2) - 150
            });
            setHoveredReservationId(reservationId);
            setHoveredReservationData(timeSlot);
            setShowReservationDetail(true);
        },
        onMouseLeave: (e) => {
            const modal = document.querySelector('.reservation-detail-modal');
            const isOverModal = modal && modal.contains(e.relatedTarget);
            if (!isOverModal) {
                setHoveredReservationId(null);
                setHoveredReservationData(null);
                setShowReservationDetail(false);
            }
        },
        onTouchStart: (e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setPopupPosition({
                top: rect.top - 120,
                left: rect.left + (rect.width / 2) - 150
            });
            setHoveredReservationId(reservationId);
            setHoveredReservationData(timeSlot);
            setShowReservationDetail(true);
        },
        onTouchEnd: () => {
            setHoveredReservationId(null);
            setHoveredReservationData(null);
            setShowReservationDetail(false);
        },
        onTouchCancel: () => {
            setHoveredReservationId(null);
            setHoveredReservationData(null);
            setShowReservationDetail(false);
        }
    });

    const parseSelectedDateOnly = (dateStr) => {
        const [y, m, d] = dateStr.split('-').map(Number);
        return new Date(y, m - 1, d);
    };

    const parseHourFromDateTime = (dateTimeStr) => {
        if (!dateTimeStr) return 0;
        const timePart = dateTimeStr.includes('T')
            ? dateTimeStr.split('T')[1].replace(/Z.*$/, '').split('+')[0]
            : dateTimeStr.split(' ')[1];
        if (!timePart) return 0;
        const parts = timePart.split(':');
        return parseInt(parts[0], 10) + parseInt(parts[1] || '0', 10) / 60;
    };

    const parseDateOnlyFromDateTime = (dateTimeStr) => {
        const datePart = dateTimeStr.split(/[ T]/)[0];
        return parseSelectedDateOnly(datePart);
    };

    const getReservationDayRange = (timeSlot, selectedDateOnly) => {
        const startDateOnly = parseDateOnlyFromDateTime(timeSlot.startTime);
        const endDateOnly = parseDateOnlyFromDateTime(timeSlot.endTime);

        if (selectedDateOnly < startDateOnly || selectedDateOnly > endDateOnly) {
            return null;
        }

        let startHour;
        let endHour;

        if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
            startHour = parseHourFromDateTime(timeSlot.startTime);
            endHour = parseHourFromDateTime(timeSlot.endTime);
        } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
            startHour = parseHourFromDateTime(timeSlot.startTime);
            endHour = 24;
        } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
            startHour = 0;
            endHour = parseHourFromDateTime(timeSlot.endTime);
        } else {
            startHour = 0;
            endHour = 24;
        }

        if (endHour === 0) {
            endHour = 24;
        }

        if (endHour <= startHour) {
            return null;
        }

        return { startHour, endHour };
    };

    const getStatusBackgroundColor = (status) => {
        switch (status) {
            case 'available':
                return 'rgba(82, 196, 26, 0.6)';
            case 'reserved':
                return '#FE9874';
            case 'unavailable':
                return 'rgba(191, 191, 191, 0.8)';
            default:
                return 'rgba(204, 204, 204, 0.3)';
        }
    };

    const buildAxisStatusSlots = (reservationSlots, axisStart, axisEnd, currentTime) => {
        const mergedSlots = [];
        let currentSlot = null;
        const sortedSlots = [...reservationSlots].sort((a, b) => a.start - b.start);

        for (const slot of sortedSlots) {
            if (!currentSlot) {
                currentSlot = { ...slot };
            } else if (slot.start <= currentSlot.end) {
                currentSlot.end = Math.max(currentSlot.end, slot.end);
            } else {
                mergedSlots.push(currentSlot);
                currentSlot = { ...slot };
            }
        }
        if (currentSlot) {
            mergedSlots.push(currentSlot);
        }

        const finalSlots = [];

        if (mergedSlots.length === 0) {
            if (currentTime >= 0) {
                if (currentTime <= axisStart) {
                    finalSlots.push({ start: axisStart, end: axisEnd, status: 'available' });
                } else if (currentTime >= axisEnd) {
                    finalSlots.push({ start: axisStart, end: axisEnd, status: 'unavailable' });
                } else {
                    finalSlots.push({ start: axisStart, end: currentTime, status: 'unavailable' });
                    finalSlots.push({ start: currentTime, end: axisEnd, status: 'available' });
                }
            } else {
                finalSlots.push({ start: axisStart, end: axisEnd, status: 'available' });
            }
            return finalSlots.filter(slot => slot.start < slot.end);
        }

        let lastEnd = axisStart;

        for (const reservedSlot of mergedSlots) {
            if (currentTime >= 0 && lastEnd < currentTime && currentTime <= reservedSlot.start) {
                const unavailableEnd = Math.min(reservedSlot.start, currentTime);
                if (unavailableEnd > lastEnd) {
                    finalSlots.push({
                        start: lastEnd,
                        end: unavailableEnd,
                        status: 'unavailable'
                    });
                }
                lastEnd = unavailableEnd;
            }

            const availableStart = currentTime >= 0 ? Math.max(lastEnd, currentTime) : lastEnd;
            if (reservedSlot.start > availableStart) {
                finalSlots.push({
                    start: availableStart,
                    end: reservedSlot.start,
                    status: 'available'
                });
            }

            finalSlots.push({
                start: reservedSlot.start,
                end: reservedSlot.end,
                status: 'reserved',
                timeSlot: reservedSlot.timeSlot
            });
            lastEnd = reservedSlot.end;
        }

        if (lastEnd < axisEnd) {
            const availableStart = currentTime >= 0 ? Math.max(lastEnd, currentTime) : lastEnd;
            if (availableStart < axisEnd) {
                finalSlots.push({
                    start: availableStart,
                    end: axisEnd,
                    status: 'available'
                });
            }
        }

        return finalSlots.filter(slot => slot.start < slot.end);
    };

    const renderTimeAxisStatusSlots = (vehicle, axisStart, axisEnd, idPrefix) => {
        const now = new Date();
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const selectedDateOnly = parseSelectedDateOnly(selectedDate);
        let currentTime = -1;
        if (selectedDateOnly.getTime() === currentDate.getTime()) {
            currentTime = now.getHours() + (now.getMinutes() / 60);
        }

        const vehicleTimes = vehicleUsageTimes
            .filter(item => item.vehicleId && vehicle.vehicleId && item.vehicleId === vehicle.vehicleId)
            .filter(item => {
                const startDateOnly = parseDateOnlyFromDateTime(item.startTime);
                const endDateOnly = parseDateOnlyFromDateTime(item.endTime);
                return selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
            })
            .filter((item, index, self) => {
                const key = `${item.startTime}-${item.endTime}`;
                return self.findIndex(t => `${t.startTime}-${t.endTime}` === key) === index;
            });

        const reservationSlots = [];
        for (const time of vehicleTimes) {
            const dayRange = getReservationDayRange(time, selectedDateOnly);
            if (!dayRange) continue;
            const clippedStart = Math.max(dayRange.startHour, axisStart);
            const clippedEnd = Math.min(dayRange.endHour, axisEnd);
            if (clippedStart < clippedEnd) {
                reservationSlots.push({
                    start: clippedStart,
                    end: clippedEnd,
                    timeSlot: time
                });
            }
        }

        const axisDuration = axisEnd - axisStart;
        const validSlots = buildAxisStatusSlots(reservationSlots, axisStart, axisEnd, currentTime);

        return validSlots.map((slot, index) => {
            const widthPercent = ((slot.end - slot.start) / axisDuration) * 100;
            return (
                <div
                    key={`${idPrefix}-${index}`}
                    className={`time-slot-status ${slot.status}`}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: `${((slot.start - axisStart) / axisDuration) * 100}%`,
                        width: widthPercent >= 100 ? '100%' : `max(${widthPercent}%, 2px)`,
                        bottom: 0,
                        backgroundColor: getStatusBackgroundColor(slot.status),
                        pointerEvents: slot.status === 'reserved' ? 'auto' : 'none',
                        cursor: slot.status === 'reserved' ? 'pointer' : undefined,
                        zIndex: slot.status === 'reserved' ? 10 : undefined,
                        borderRadius: '4px',
                        padding: 0,
                        margin: 0,
                        boxSizing: 'border-box'
                    }}
                    title={slot.status === 'reserved' && slot.timeSlot ? `${formatTime(slot.timeSlot.startTime)} - ${formatTime(slot.timeSlot.endTime)}` : undefined}
                    {...(slot.status === 'reserved' && slot.timeSlot ? bindReservationDetailEvents(slot.timeSlot, `${vehicle.vehicleId}-${idPrefix}-${index}`) : {})}
                ></div>
            );
        });
    };
    
    // 计算时间槽的位置和宽度，统一使用24小时（支持分钟）
    const calculateTimeSlotPosition = (startTime, endTime) => {
        // 提取开始和结束日期
        const startDate = startTime.split(' ')[0];
        const endDate = endTime.split(' ')[0];
        
        // 检测是否跨天
        const isCrossDay = startDate !== endDate;
        
        // 直接从字符串中提取日期和时间，确保使用正确的日期和小时
        const startParts = startTime.split(' ')[1].split(':');
        const endParts = endTime.split(' ')[1].split(':');
        
        // 提取开始和结束时间（包含分钟，转换为小时小数）
        const startHour = parseInt(startParts[0], 10) + parseInt(startParts[1], 10) / 60;
        let endHour = parseInt(endParts[0], 10) + parseInt(endParts[1], 10) / 60;
        
        // 如果跨天，结束时间设为24点
        if (isCrossDay) {
            endHour = 24;
        }
        
        // 统一使用24小时
        const maxHours = 24;
        
        // 确保位置在0-24小时范围内
        const normalizedStart = Math.max(0, Math.min(maxHours, startHour));
        const normalizedEnd = Math.max(0, Math.min(maxHours, endHour));
        
        // 计算开始位置：startHour / 24 * 100%
        const left = (normalizedStart / maxHours) * 100;
        
        // 计算宽度，确保只显示到结束时间点
        const hoursDuration = normalizedEnd - normalizedStart;
        const width = (hoursDuration / maxHours) * 100;
        
        return { left: `${left}%`, width: `${width}%` };
    };
    
    // 获取车辆列表
    const fetchVehicles = async () => {
        try {
            setLoading(true);
            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/getVehicleDeviceListByCompanyId`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.data && response.data.code === 200) {
                setVehicles(response.data.data || []);
            } else {
                setVehicles([]);
            }
        } catch (error) {
            console.error('获取车辆列表失败:', error);
            setVehicles([]);
        } finally {
            setLoading(false);
        }
    };
    
    // 获取车辆已预约时间
    const fetchVehicleAvailableTime = async (plateNumber = '') => {
        try {
            const params = {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                startTime: `${selectedDate} 00:00:00`,
                endTime: `${selectedDate} 23:59:59`
            };
            
            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/getVehicleAvailableTime`, params, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.data && response.data.code === 200) {
                let usageTimes = response.data.data || [];
                
                // 保留所有在选定日期范围内有效的预约时间
                const filteredUsageTimes = usageTimes.filter(item => {
                    // 检查选定日期是否在预约时间范围内（包括开始日期、中间日期和结束日期）
                    const startDateTime = new Date(item.startTime);
                    const endDateTime = new Date(item.endTime);
                    const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                    const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
                    
                    const dateParts = selectedDate.split('-');
                    const selectedDateOnly = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
                    
                    return selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
                });
                
                // 如果指定了车牌号，进一步筛选该车辆的预约时间
                if (plateNumber) {
                    // 先找到对应的车辆ID
                    const targetVehicle = vehicles.find(v => v.plateNumber === plateNumber);
                    if (targetVehicle) {
                        usageTimes = filteredUsageTimes.filter(item => {
                            return item.vehicleId === targetVehicle.vehicleId;
                        });
                    } else {
                        usageTimes = [];
                    }
                } else {
                    usageTimes = filteredUsageTimes;
                }
                
                setVehicleUsageTimes(usageTimes);
            } else {
                setVehicleUsageTimes([]);
            }
        } catch (error) {
            console.error('获取车辆已预约时间失败:', error);
            setVehicleUsageTimes([]);
        }
    };
    
    // 获取车辆分组列表
    const fetchVehicleGroups = async () => {
        try {
            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/getVehicleGroups`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.data && response.data.code === 200) {
                setVehicleGroups(response.data.data || []);
            }
        } catch (error) {
            console.error('获取车辆分组失败:', error);
        }
    };
    
    // 获取座位数列表
    const fetchCarSeats = async () => {
        try {
            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/getCarSeats`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.data && response.data.code === 200) {
                setAvailableSeats([{ seatName: '全部' }, ...(response.data.data || [])]);
            }
        } catch (error) {
            console.error('获取座位数失败:', error);
        }
    };

    // 选择车辆
    const handleVehicleSelect = (vehicle) => {
        // 处理车辆分组信息，确保传递中文名称
        const vehicleWithGroupName = {
            ...vehicle,
            group: typeof vehicle.group === 'number' ? getGroupNameById(vehicle.group) : vehicle.group
        };

        // 计算当前车辆在选定日期的可预约时间信息
        const availableTimeInfo = calculateAvailableTimeInfo(vehicle);

        // 跳转到新的时间选择页面，传递车辆信息、日期和可预约时间作为URL参数
        const vehicleStr = JSON.stringify(vehicleWithGroupName);
        const encodedVehicle = encodeURIComponent(vehicleStr);
        const encodedDate = encodeURIComponent(selectedDate);
        const encodedAvailableTime = availableTimeInfo ? encodeURIComponent(JSON.stringify(availableTimeInfo)) : '';
        navigate(`/time-select?vehicle=${encodedVehicle}&date=${encodedDate}${encodedAvailableTime ? `&availableTime=${encodedAvailableTime}` : ''}`);
    };
    // 计算车辆在选定日期的可预约时间信息
    const calculateAvailableTimeInfo = (vehicle) => {
        const dateParts = selectedDate.split('-');
        const selectedDateOnly = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
        const now = new Date();
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        let currentTime = -1;
        if (selectedDateOnly.getTime() === currentDate.getTime()) {
            currentTime = now.getHours() + (now.getMinutes() / 60);
        }

        const vehicleTimesForSummary = vehicleUsageTimes
            .filter(item => {
                const isVehicleMatch = item.vehicleId && vehicle.vehicleId && item.vehicleId === vehicle.vehicleId;
                const startDateTime = new Date(item.startTime);
                const endDateTime = new Date(item.endTime);
                const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
                const isDateInRange = selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
                return isVehicleMatch && isDateInRange;
            })
            .filter((item, index, self) => {
                const key = `${item.startTime}-${item.endTime}`;
                return self.findIndex(t => `${t.startTime}-${t.endTime}` === key) === index;
            });

        // 计算可预约时间段
        const mergedSlots = [];
        const sortedSlots = [...vehicleTimesForSummary].sort((a, b) => {
            const aStart = new Date(a.startTime);
            const bStart = new Date(b.startTime);
            return aStart - bStart;
        });

        for (const time of sortedSlots) {
            const startDateTime = new Date(time.startTime);
            const endDateTime = new Date(time.endTime);
            const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
            const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());

            let startHour, endHour;
            if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
                startHour = startDateTime.getHours() + startDateTime.getMinutes() / 60;
                endHour = endDateTime.getHours() + endDateTime.getMinutes() / 60;
            } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
                startHour = startDateTime.getHours() + startDateTime.getMinutes() / 60;
                endHour = 24;
            } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
                startHour = 0;
                endHour = endDateTime.getHours() + endDateTime.getMinutes() / 60;
            } else {
                startHour = 0;
                endHour = 24;
            }

            if (mergedSlots.length === 0) {
                mergedSlots.push({ start: startHour, end: endHour });
            } else {
                const lastSlot = mergedSlots[mergedSlots.length - 1];
                if (startHour <= lastSlot.end) {
                    lastSlot.end = Math.max(lastSlot.end, endHour);
                } else {
                    mergedSlots.push({ start: startHour, end: endHour });
                }
            }
        }

        // 生成最终的时间段
        const finalSlots = [];
        let lastEnd = 0;

        for (const reservedSlot of mergedSlots) {
            if (currentTime >= 0 && lastEnd < currentTime) {
                const unavailableEnd = Math.min(reservedSlot.start, currentTime);
                if (unavailableEnd > lastEnd) {
                    finalSlots.push({ start: lastEnd, end: unavailableEnd, status: 'unavailable' });
                }
                lastEnd = unavailableEnd;
            }

            if (reservedSlot.start > lastEnd) {
                finalSlots.push({ start: lastEnd, end: reservedSlot.start, status: 'available' });
            }

            const reservedStart = Math.max(reservedSlot.start, currentTime >= 0 ? currentTime : 0);
            if (reservedStart < reservedSlot.end) {
                finalSlots.push({ start: reservedStart, end: reservedSlot.end, status: 'reserved' });
            }

            lastEnd = reservedSlot.end;
        }

        if (lastEnd < 24) {
            if (currentTime >= 0 && lastEnd < currentTime) {
                finalSlots.push({ start: lastEnd, end: currentTime, status: 'unavailable' });
                lastEnd = currentTime;
            }
            if (lastEnd < 24) {
                finalSlots.push({ start: lastEnd, end: 24, status: 'available' });
            }
        }

        if (mergedSlots.length === 0) {
            if (currentTime >= 0) {
                finalSlots.push({ start: 0, end: currentTime, status: 'unavailable' });
                finalSlots.push({ start: currentTime, end: 24, status: 'available' });
            } else {
                finalSlots.push({ start: 0, end: 24, status: 'available' });
            }
        }

        // 格式化时间段显示
        const formatHour = (hour) => {
            const h = Math.floor(hour);
            const m = Math.round((hour - h) * 60);
            return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        };

        // 最小时间间隔阈值（单位：小时），小于这个值的时间段不显示
        const MIN_TIME_THRESHOLD = 5 / 60; // 5分钟

        // 对finalSlots进行去重，避免相同时间段重复显示
        const uniqueSlots = [];
        const seenTimeRanges = new Set();

        finalSlots.forEach(slot => {
            const timeKey = `${slot.start}-${slot.end}`;
            if (!seenTimeRanges.has(timeKey)) {
                seenTimeRanges.add(timeKey);
                uniqueSlots.push(slot);
            }
        });

        // 过滤掉时间间隔过短的可预约时间段
        const availableSlots = uniqueSlots
            .filter(s => s.status === 'available')
            .filter(s => (s.end - s.start) >= MIN_TIME_THRESHOLD);

        if (availableSlots.length === 0) {
            return { text: '🚫 今日无空闲时段', slots: [] };
        }

        if (availableSlots.length === 1) {
            return {
                text: `✅ 可预约：${formatHour(availableSlots[0].start)}-${formatHour(availableSlots[0].end)}`,
                slots: availableSlots.map(slot => ({
                    start: formatHour(slot.start),
                    end: formatHour(slot.end)
                }))
            };
        }

        return {
            text: `✅ 可预约：${availableSlots.map(slot => `${formatHour(slot.start)}-${formatHour(slot.end)}`).join('、')}`,
            slots: availableSlots.map(slot => ({
                start: formatHour(slot.start),
                end: formatHour(slot.end)
            }))
        };
    };


    // 选择日期
    const handleDateSelect = (date) => {
        setSelectedDate(date);
    };
    
    // 选择分组
    const handleGroupSelect = (groupName) => {
        setSelectedGroup(groupName);
    };
    
    // 选择座位数
    const handleSeatsSelect = (seatName) => {
        setSelectedSeats(seatName);
    };
    
    // 初始化数据
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchVehicles();
        fetchVehicleGroups();
        fetchCarSeats();
        // 无论车辆数据是否获取完成，都直接获取当天的预约时间
        fetchVehicleAvailableTime();
    }, []);
    
    // 车辆数据获取完成后，重新获取当天的预约时间（确保数据同步）
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (vehicles.length > 0) {
            fetchVehicleAvailableTime();
        }
    }, [vehicles]);
    
    // 日期变化时重新获取数据
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchVehicleAvailableTime();
    }, [selectedDate]);
    
    // 点击外部关闭下拉框
    useEffect(() => {
        const handleClickOutside = (event) => {
            // 检查是否点击了下拉框外部
            if (
                isGroupDropdownOpen && 
                filterContainerRef.current && 
                !filterContainerRef.current.contains(event.target)
            ) {
                setIsGroupDropdownOpen(false);
            }
            
            if (
                isSeatsDropdownOpen && 
                filterContainerRef.current && 
                !filterContainerRef.current.contains(event.target)
            ) {
                setIsSeatsDropdownOpen(false);
            }
        };
        
        // 添加事件监听器
        document.addEventListener('click', handleClickOutside);
        
        // 清理事件监听器
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isGroupDropdownOpen, isSeatsDropdownOpen]);
    
    // 从预约时间数据中提取所有车辆信息
    const extractVehiclesFromUsageTimes = useCallback(() => {
        // 从vehicleUsageTimes中提取唯一的车辆信息
        const uniqueVehicles = [];
        const seenPlateNumbers = new Set();
        
        vehicleUsageTimes.forEach(usage => {
            if (usage.plateNumber && !seenPlateNumbers.has(usage.plateNumber)) {
                seenPlateNumbers.add(usage.plateNumber);
                // 创建车辆对象，使用从usage中提取的数据
                uniqueVehicles.push({
                    plateNumber: usage.plateNumber,
                    vehicleType: usage.modelName || '未知车型',
                    vehicleId: usage.vehicleId,
                    imageUrl: '',
                    seats: 5,
                    group: '',
                    status: 'available'
                });
            }
        });
        
        return uniqueVehicles;
    }, [vehicleUsageTimes]);
    
    // 合并车辆列表和从预约时间中提取的车辆信息
    const allVehicles = useCallback(() => {
        // 如果直接获取的车辆列表有数据，使用它
        if (vehicles.length > 0) {
            return vehicles;
        }
        // 否则，从预约时间数据中提取车辆信息
        return extractVehiclesFromUsageTimes();
    }, [vehicles, extractVehiclesFromUsageTimes]);
    
    // 辅助函数：将分组ID转换为分组名称
    const getGroupNameById = (groupId) => {
        // 查找对应的分组名称
        const group = vehicleGroups.find(g => g.groupId === groupId);
        return group ? group.groupName : '';
    };

    // 辅助函数：根据分组ID返回对应的图标（根据重要程度）
    const getGroupIconById = (groupId) => {
        // 假设 groupId 越小越重要
        // groupId 1: ic1.png (最重要)
        // groupId 2: ic2.png (中等重要)
        // 其他: ic3.png (一般重要)
        if (groupId === 1) return ic1;
        if (groupId === 2) return ic2;
        return ic3;
    };
    
    // 过滤车辆列表
    const filteredVehicles = allVehicles().filter(vehicle => {
        // 按车牌号搜索过滤
        const matchesSearch = vehicle && vehicle.plateNumber && 
            vehicle.plateNumber.toLowerCase().includes(searchTerm.toLowerCase());
        
        // 按分组过滤
        // 转换车辆的group ID为分组名称，然后与selectedGroup比较
        const vehicleGroupName = typeof vehicle.group === 'number' ? getGroupNameById(vehicle.group) : vehicle.group;
        const matchesGroup = !selectedGroup || vehicleGroupName === selectedGroup;
        
        // 按座位数过滤
        let matchesSeats = true;
        if (selectedSeats !== '全部' && selectedSeats !== '座位数') {
            // 从座位名称中提取数字部分
            const selectedSeatsValue = parseInt(selectedSeats.replace(/[^0-9]/g, ''));
            
            // 检查车辆座位数是否匹配
            matchesSeats = vehicle && vehicle.seats === selectedSeatsValue;
        }
        
        return matchesSearch && matchesGroup && matchesSeats;
    });
    
    // 生成未来7天的日期
    const next7Days = generateNext7Days();

    // 获取车辆在选定日期的状态
    const getVehicleStatus = (vehicle) => {
        const vehicleTimes = vehicleUsageTimes.filter(item => {
            if (item.vehicleId !== vehicle.vehicleId) {
                return false;
            }

            // 检查选定日期是否在预约时间范围内（包括开始日期、中间日期和结束日期）
            const startDateTime = new Date(item.startTime);
            const endDateTime = new Date(item.endTime);
            const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
            const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());

            const dateParts = selectedDate.split('-');
            const selectedDateOnly = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);

            return selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
        });

        if (vehicleTimes.length === 0) {
            return 'available';
        }

        // 检查是否全天都被预约（所有时间段都已预约）
        const dateParts = selectedDate.split('-');
        const selectedDateOnly = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
        const now = new Date();
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        let currentTime = -1;

        if (selectedDateOnly.getTime() === currentDate.getTime()) {
            currentTime = now.getHours() + (now.getMinutes() / 60);
        }

        // 合并已预约时间段
        const mergedSlots = [];
        const sortedSlots = [...vehicleTimes].sort((a, b) => {
            const aStart = new Date(a.startTime);
            const bStart = new Date(b.startTime);
            return aStart - bStart;
        });

        for (const time of sortedSlots) {
            const startDateTime = new Date(time.startTime);
            const endDateTime = new Date(time.endTime);
            const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
            const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());

            let startHour, endHour;
            if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
                startHour = startDateTime.getHours() + startDateTime.getMinutes() / 60;
                endHour = endDateTime.getHours() + endDateTime.getMinutes() / 60;
            } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
                startHour = startDateTime.getHours() + startDateTime.getMinutes() / 60;
                endHour = 24;
            } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
                startHour = 0;
                endHour = endDateTime.getHours() + endDateTime.getMinutes() / 60;
            } else {
                startHour = 0;
                endHour = 24;
            }

            if (mergedSlots.length === 0) {
                mergedSlots.push({ start: startHour, end: endHour });
            } else {
                const lastSlot = mergedSlots[mergedSlots.length - 1];
                if (startHour <= lastSlot.end) {
                    lastSlot.end = Math.max(lastSlot.end, endHour);
                } else {
                    mergedSlots.push({ start: startHour, end: endHour });
                }
            }
        }

        // 计算可预约时间段
        let lastEnd = 0;
        let hasAvailableTime = false;

        for (const reservedSlot of mergedSlots) {
            if (currentTime >= 0 && lastEnd < currentTime) {
                lastEnd = Math.min(reservedSlot.start, currentTime);
            }

            if (reservedSlot.start > lastEnd) {
                hasAvailableTime = true;
                break;
            }

            const reservedStart = Math.max(reservedSlot.start, currentTime >= 0 ? currentTime : 0);
            if (reservedStart < reservedSlot.end) {
                lastEnd = reservedSlot.end;
            }
        }

        if (lastEnd < 24) {
            if (currentTime >= 0 && lastEnd < currentTime) {
                lastEnd = currentTime;
            }
            if (lastEnd < 24) {
                hasAvailableTime = true;
            }
        }

        // 如果有可预约时间，返回partial（部分可预约），否则返回reserved（已预约）
        return hasAvailableTime ? 'partial' : 'reserved';
    };



    return (
        <div className="vehicle-select-page">
            <div className="vehicle-select-header">
                <h2>车辆预约信息</h2>
                <div className="header-right"></div>
            </div>
            
            {/* 日期选择器 */}
            <div className="date-selector">
                {next7Days.map(day => (
                    <div 
                        key={day.date}
                        className={`date-item ${selectedDate === day.date ? 'selected' : ''}`}
                        onClick={() => handleDateSelect(day.date)}
                    >
                        <div className="date-day">{day.day}</div>
                        <div className="date-weekday">{day.weekDay}</div>
                    </div>
                ))}
            </div>
            
            {/* 搜索框 */}
            {/* <div className="search-container">
                <input 
                    type="text" 
                    placeholder="输入车牌号"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                />
            </div> */}
            
            {/* 筛选条件 */}
            <div className="filter-container1" ref={filterContainerRef}>
                {/* 分组筛选 */}
                <div ref={groupFilterRef} className={`filter-item1 ${isGroupDropdownOpen ? 'active' : ''}`} onClick={() => {
                    setIsGroupDropdownOpen(!isGroupDropdownOpen);
                    setIsSeatsDropdownOpen(false);
                }}>
                    <span className="filter-arrow">{selectedGroup || '请选择使用场景'} </span>
                    <img src={arrowDownIcon} alt="arrow down" className="arrow-icon" />
                    {isGroupDropdownOpen && (
                        <DropdownPortal isOpen={isGroupDropdownOpen} filterRef={groupFilterRef}>
                            <div className="filter-dropdown">
                                <div 
                                    className="filter-option"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleGroupSelect('');
                                        setIsGroupDropdownOpen(false);
                                    }}
                                >
                                    全部
                                </div>
                                {vehicleGroups.map(group => (
                                    <div 
                                        key={group.groupId} 
                                        className="filter-option"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleGroupSelect(group.groupName);
                                            setIsGroupDropdownOpen(false);
                                        }}
                                    >
                                        {group.groupName}
                                    </div>
                                ))}
                            </div>
                        </DropdownPortal>
                    )}
                </div>
                
                {/* 座位数筛选 */}
                <div ref={seatsFilterRef} className={`filter-item1 ${isSeatsDropdownOpen ? 'active' : ''}`} onClick={() => {
                    setIsSeatsDropdownOpen(!isSeatsDropdownOpen);
                    setIsGroupDropdownOpen(false);
                }}>
                    <span className="filter-arrow">{selectedSeats === '全部' ? '座位数' : selectedSeats} </span>
                     <img src={arrowDownIcon} alt="arrow down" className="arrow-icon" />
                    {isSeatsDropdownOpen && (
                        <DropdownPortal isOpen={isSeatsDropdownOpen} filterRef={seatsFilterRef}>
                            <div className="filter-dropdown">
                                {availableSeats.map((seat, index) => (
                                    <div 
                                        key={index} 
                                        className="filter-option"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSeatsSelect(seat.seatName);
                                            setIsSeatsDropdownOpen(false);
                                        }}
                                    >
                                        {seat.seatName}
                                    </div>
                                ))}
                            </div>
                        </DropdownPortal>
                    )}
                </div>
                  {/* 搜索框 */}
                <div className="filter-item1 filter-item2">
                    <input 
                    type="text" 
                    placeholder="输入车牌"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                />
                </div>
            </div>
            
            {/* 状态说明 */}
            <div className="status-legend">
                <div className="status-item">
                    <span className="status-dot1 available"></span>
                    <span>可预约</span>
                    <div className="book-num">
                        {!loading && filteredVehicles.filter(v => getVehicleStatus(v) === 'available').length}
                    </div>
                </div>
                <div className="status-item">
                    <span className="status-dot1 reserved"></span>
                    <span>已预约</span>
                    <div className="partial-num">
                        {!loading && filteredVehicles.filter(v => getVehicleStatus(v) === 'partial').length}
                    </div>
                </div>
                <div className="status-item">
                    <span className="status-dot1 unavailable"></span>
                    <span>不可预约</span>
                    <div className="unavailable-num">
                        {!loading && filteredVehicles.filter(v => getVehicleStatus(v) === 'reserved').length}
                    </div>
                </div>
            </div>
            
            {/* 车辆列表 */}
            <div className="vehicles-list">
                {loading ? (
                    <div className="loading-indicator">
                        <p>正在加载车辆数据...</p>
                    </div>
                ) : filteredVehicles.length === 0 ? (
                    <div className="no-vehicles">
                        <p>暂无车辆数据</p>
                    </div>
                ) : (
                    filteredVehicles.map(vehicle => {
                        const status = getVehicleStatus(vehicle);
                        
                        // 检查车辆ID是否匹配，并过滤重复数据
                const vehicleTimes = vehicleUsageTimes
                    .filter(item => {
                        const isVehicleMatch = item.vehicleId && vehicle.vehicleId && 
                            item.vehicleId === vehicle.vehicleId;
                        
                        // 检查选定日期是否在预约时间范围内（包括开始日期、中间日期和结束日期）
                        const startDateTime = new Date(item.startTime);
                        const endDateTime = new Date(item.endTime);
                        const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                        const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
                        
                        const dateParts = selectedDate.split('-');
                        const selectedDateOnly = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
                        
                        const isDateInRange = selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
                        
                        return isVehicleMatch && isDateInRange;
                    })
                    // 去重：根据startTime和endTime判断是否为重复数据
                    .filter((item, index, self) => {
                        const key = `${item.startTime}-${item.endTime}`;
                        return self.findIndex(t => `${t.startTime}-${t.endTime}` === key) === index;
                    });
                        
                        return (
                            <div 
                            key={vehicle.plateNumber} 
                            className="vehicle-wrapper"
                            >
                            {/* 车辆信息行：图片 + 信息 + 状态 */}
                            <div 
                                className="vehicle-item"
                            >
                                {/* // onClick={() => handleVehicleSelect(vehicle)} */}
                            
                                {/* 左侧车辆图片 */}
                                <div className="vehicle_box">
                                    <div className="vehicle-image">
                                        {vehicle.imageUrl ? (
                                            <img 
                                                src={vehicle.imageUrl} 
                                                alt={vehicle.plateNumber} 
                                                className="vehicle-image-img"
                                            />
                                        ) : (
                                            <div className="default-vehicle-image">🚗</div>
                                        )}
                                        
                                    </div>
                                    {/* 右侧车辆信息 */}
                                    <div className="vehicle-right-info">
                                        {/* 第一行：车牌号 */}
                                        <div className="vehicle-info-line">
                                            <div className="vehicle-plate-number">{vehicle.plateNumber}</div>
                                        </div>
                                        {/* 第二行：品牌型号 + 座位数 */}
                                        <div className="vehicle-info-line">
                                            <div className="vehicle-type">{vehicle.modelName || vehicle.vehicleType || '未知车型'}</div>
                                            <div className="vehicle-seats">{vehicle.seats || 5}座</div>
                                        </div>
                                        {/* 第三行：使用场景 */}
                                        {(() => {
                                            const vehicleGroupName = typeof vehicle.group === 'number' ? getGroupNameById(vehicle.group) : vehicle.group;
                                            const vehicleGroupIcon = typeof vehicle.group === 'number' ? getGroupIconById(vehicle.group) : ic3;
                                            return vehicleGroupName && (
                                                <div className="vehicle-info-line">
                                                    
                                                    <div className={`vehicle-group${vehicle.group}`}><img src={vehicleGroupIcon} className="vehicle-group-img" alt="group-icon" /><div>{vehicleGroupName}</div></div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                                
                                
                                {/* 状态标签 */}
                                <div className="vehicle-status-container">
                                    <div onClick={() => handleVehicleSelect(vehicle)} className={`vehicle-status-indicator2 ${status}`}>去预约</div>
                                </div>
                            </div>

                                {/* 24小时时间轴 - 单独在下方 */}
                                <div className="time-axis-container">
                                    {/* 上半部分：0-12小时 */}
                                    <div className="time-slot-section">
                                        <div className="time-label2 top">
                                            <span>0</span>
                                            <span>1</span>
                                            <span>2</span>
                                            <span>3</span>
                                            <span>4</span>
                                            <span>5</span>
                                            <span>6</span>
                                            <span>7</span>
                                            <span>8</span>
                                            <span>9</span>
                                            <span>10</span>
                                            <span>11</span>
                                            <span>12</span>
                                        </div>
                                        <div 
                                            className={`time-axis upper ${status === 'unavailable' ? 'unavailable' : ''}`}
                                            style={{ backgroundColor: 'transparent' }}
                                        >
                                    {/* 渲染不同状态的时段 */}
                                    {(() => {
                                        const now = new Date();
                                        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                        const dateParts = selectedDate.split('-');
                                        const selectedDateOnly = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);

                                        const vehicleTimes = vehicleUsageTimes
                                            .filter(item => {
                                                const isVehicleMatch = item.vehicleId && vehicle.vehicleId &&
                                                    item.vehicleId === vehicle.vehicleId;

                                                const startDateTime = new Date(item.startTime);
                                                const endDateTime = new Date(item.endTime);
                                                const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                                                const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());

                                                const isDateInRange = selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;

                                                return isVehicleMatch && isDateInRange;
                                            })
                                            .filter((item, index, self) => {
                                                const key = `${item.startTime}-${item.endTime}`;
                                                return self.findIndex(t => `${t.startTime}-${t.endTime}` === key) === index;
                                            });

                                        const getReservationSlotsForDate = () => {
                                            const slots = [];

                                            for (const time of vehicleTimes) {
                                                const startDateTime = new Date(time.startTime);
                                                const endDateTime = new Date(time.endTime);

                                                const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                                                const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());

                                                if (selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly) {
                                                    if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
                                                        const startParts = time.startTime.split(' ')[1].split(':');
                                                        const startHour = parseInt(startParts[0], 10) + parseInt(startParts[1], 10) / 60;
                                                        const endParts = time.endTime.split(' ')[1].split(':');
                                                        const endHour = parseInt(endParts[0], 10) + parseInt(endParts[1], 10) / 60;
                                                        // 只处理0-12小时范围
                                                        if (endHour > 0 && startHour < 12) {
                                                            slots.push({ start: Math.max(startHour, 0), end: Math.min(endHour, 12), timeSlot: time });
                                                        }
                                                    } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
                                                        const startParts = time.startTime.split(' ')[1].split(':');
                                                        const startHour = parseInt(startParts[0], 10) + parseInt(startParts[1], 10) / 60;
                                                        if (startHour < 12) {
                                                            slots.push({ start: startHour, end: 12, timeSlot: time });
                                                        }
                                                    } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
                                                        const endParts = time.endTime.split(' ')[1].split(':');
                                                        const endHour = parseInt(endParts[0], 10) + parseInt(endParts[1], 10) / 60;
                                                        if (endHour > 0) {
                                                            slots.push({ start: 0, end: Math.min(endHour, 12), timeSlot: time });
                                                        }
                                                    } else {
                                                        slots.push({ start: 0, end: 12, timeSlot: time });
                                                    }
                                                }
                                            }

                                            return slots;
                                        };

                                        const reservationSlots = getReservationSlotsForDate();

                                        if (reservationSlots.length > 0) {
                                            let currentTime = -1;

                                            if (selectedDateOnly.getTime() === currentDate.getTime()) {
                                                currentTime = now.getHours() + (now.getMinutes() / 60);
                                            }

                                            const mergedSlots = [];
                                            let currentSlot = null;

                                            const sortedSlots = [...reservationSlots].sort((a, b) => a.start - b.start);

                                            for (const slot of sortedSlots) {
                                                if (!currentSlot) {
                                                    currentSlot = { ...slot };
                                                } else {
                                                    if (slot.start <= currentSlot.end) {
                                                        currentSlot.end = Math.max(currentSlot.end, slot.end);
                                                    } else {
                                                        mergedSlots.push(currentSlot);
                                                        currentSlot = { ...slot };
                                                    }
                                                }
                                            }

                                            if (currentSlot) {
                                                mergedSlots.push(currentSlot);
                                            }

                                            const finalSlots = [];
                                            let lastEnd = 0;

                                            for (const reservedSlot of mergedSlots) {
                                                if (currentTime >= 0 && lastEnd < currentTime) {
                                                    if (currentTime < reservedSlot.start) {
                                                        // 当前时间在预约之前
                                                        if (currentTime > lastEnd) {
                                                            finalSlots.push({
                                                                start: lastEnd,
                                                                end: currentTime,
                                                                status: 'unavailable'
                                                            });
                                                            lastEnd = currentTime;
                                                        }
                                                    } else if (currentTime < reservedSlot.end) {
                                                        // 当前时间在预约内部
                                                        if (reservedSlot.start > lastEnd) {
                                                            finalSlots.push({
                                                                start: lastEnd,
                                                                end: reservedSlot.start,
                                                                status: 'unavailable'
                                                            });
                                                            lastEnd = reservedSlot.start;
                                                        }
                                                    } else {
                                                        // 当前时间在预约之后
                                                        if (reservedSlot.end > lastEnd) {
                                                            finalSlots.push({
                                                                start: lastEnd,
                                                                end: reservedSlot.end,
                                                                status: 'unavailable'
                                                            });
                                                            lastEnd = reservedSlot.end;
                                                        }
                                                        continue;
                                                    }
                                                }

                                                if (reservedSlot.start > lastEnd) {
                                                    finalSlots.push({
                                                        start: lastEnd,
                                                        end: reservedSlot.start,
                                                        status: 'available'
                                                    });
                                                    lastEnd = reservedSlot.start;
                                                }

                                                finalSlots.push({
                                                    start: reservedSlot.start,
                                                    end: reservedSlot.end,
                                                    status: 'reserved',
                                                    timeSlot: reservedSlot.timeSlot
                                                });
                                                lastEnd = reservedSlot.end;
                                            }

                                            if (lastEnd < 12) {
                                                finalSlots.push({
                                                    start: lastEnd,
                                                    end: 12,
                                                    status: 'available'
                                                });
                                            }

                                            const validSlots = finalSlots.filter(slot => slot.start < slot.end);

                                            return validSlots.map((slot, index) => {
                                                const left = `${(slot.start / 12) * 100}%`;
                                                const width = `${((slot.end - slot.start) / 12) * 100}%`;

                                                let backgroundColor;
                                                switch (slot.status) {
                                                    case 'available':
                                                        backgroundColor = 'rgba(82, 196, 26, 0.6)';
                                                        break;
                                                    case 'reserved':
                                                        backgroundColor = '#FE9874';
                                                        break;
                                                    case 'unavailable':
                                                        backgroundColor = 'rgba(191, 191, 191, 0.8)';
                                                        break;
                                                    default:
                                                        backgroundColor = 'rgba(204, 204, 204, 0.3)';
                                                }

                                                return (
                                                    <div
                                                        key={index}
                                                        className={`time-slot-status ${slot.status}`}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: left,
                                                            width: ((slot.end - slot.start) / 12) * 100 >= 100 ? '100%' : `max(${((slot.end - slot.start) / 12) * 100}%, 2px)`,
                                                            bottom: 0,
                                                            backgroundColor: backgroundColor,
                                                            pointerEvents: slot.status === 'reserved' ? 'auto' : 'none',
                                                            cursor: slot.status === 'reserved' ? 'pointer' : undefined,
                                                            zIndex: slot.status === 'reserved' ? 10 : undefined,
                                                            borderRadius: '0px',
                                                            padding: 0,
                                                            margin: 0,
                                                            boxSizing: 'border-box'
                                                        }}
                                                        title={slot.status === 'reserved' && slot.timeSlot ? `${formatTime(slot.timeSlot.startTime)} - ${formatTime(slot.timeSlot.endTime)}` : undefined}
                                                        {...(slot.status === 'reserved' && slot.timeSlot ? bindReservationDetailEvents(slot.timeSlot, `${vehicle.vehicleId}-upper-${index}`) : {})}
                                                    ></div>
                                                );
                                            });
                                        } else {
                                            let currentTime = -1;

                                            if (selectedDateOnly.getTime() === currentDate.getTime()) {
                                                currentTime = now.getHours() + (now.getMinutes() / 60);
                                            }

                                            const validSlots = [];

                                            if (currentTime >= 0) {
                                                // 只处理0-12小时范围
                                                if (currentTime < 12) {
                                                    validSlots.push({
                                                        start: 0,
                                                        end: currentTime,
                                                        status: 'unavailable'
                                                    });
                                                    validSlots.push({
                                                        start: currentTime,
                                                        end: 12,
                                                        status: 'available'
                                                    });
                                                } else {
                                                    // 当前时间在12点之后，0-12全部不可预约
                                                    validSlots.push({
                                                        start: 0,
                                                        end: 12,
                                                        status: 'unavailable'
                                                    });
                                                }
                                            } else {
                                                validSlots.push({
                                                    start: 0,
                                                    end: 12,
                                                    status: 'available'
                                                });
                                            }

                                            return validSlots.map((slot, index) => {
                                                const left = `${(slot.start / 12) * 100}%`;
                                                const width = `${((slot.end - slot.start) / 12) * 100}%`;

                                                let backgroundColor;
                                                switch (slot.status) {
                                                    case 'available':
                                                        backgroundColor = 'rgba(82, 196, 26, 0.6)';
                                                        break;
                                                    case 'reserved':
                                                        backgroundColor = '#FE9874';
                                                        break;
                                                    case 'unavailable':
                                                        backgroundColor = 'rgba(191, 191, 191, 0.8)';
                                                        break;
                                                    default:
                                                        backgroundColor = 'rgba(204, 204, 204, 0.3)';
                                                }

                                                return (
                                                    <div
                                                        key={index}
                                                        className={`time-slot-status ${slot.status}`}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: left,
                                                            width: ((slot.end - slot.start) / 12) * 100 >= 100 ? '100%' : `max(${((slot.end - slot.start) / 12) * 100}%, 2px)`,
                                                            bottom: 0,
                                                            backgroundColor: backgroundColor,
                                                            pointerEvents: 'none',
                                                            borderRadius: '4px',
                                                            padding: 0,
                                                            margin: 0,
                                                            boxSizing: 'border-box'
                                                        }}
                                                    ></div>
                                                );
                                            });
                                        }
                                    })()}

                                        </div>
                                    </div>
                                    {/* 下半部分：13-24小时 */}
                                    <div className="time-slot-section time-slot-section2">
                                        
                                        <div 
                                            className={`time-axis lower ${status === 'unavailable' ? 'unavailable' : ''}`}
                                            style={{ backgroundColor: 'transparent' }}
                                        >
                                            {/* 渲染不同状态的时段 - 下半部分 */}
                                            {(() => {
                                                const now = new Date();
                                                const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                                const dateParts = selectedDate.split('-');
                                                const selectedDateOnly = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);

                                                const vehicleTimesLower = vehicleUsageTimes
                                                    .filter(item => {
                                                        const isVehicleMatch = item.vehicleId && vehicle.vehicleId &&
                                                            item.vehicleId === vehicle.vehicleId;
                                                        const startDateTime = new Date(item.startTime);
                                                        const endDateTime = new Date(item.endTime);
                                                        const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                                                        const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
                                                        const isDateInRange = selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
                                                        return isVehicleMatch && isDateInRange;
                                                    })
                                                    .filter((item, index, self) => {
                                                        const key = `${item.startTime}-${item.endTime}`;
                                                        return self.findIndex(t => `${t.startTime}-${t.endTime}` === key) === index;
                                                    });

                                                const getReservationSlotsForDateLower = () => {
                                                    const slots = [];

                                                    for (const time of vehicleTimesLower) {
                                                        const startDateTime = new Date(time.startTime);
                                                        const endDateTime = new Date(time.endTime);
                                                        const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                                                        const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());

                                                        if (selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly) {
                                                            if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
                                                                const startParts = time.startTime.split(' ')[1].split(':');
                                                                const startHour = parseInt(startParts[0], 10) + parseInt(startParts[1], 10) / 60;
                                                                const endParts = time.endTime.split(' ')[1].split(':');
                                                                const endHour = parseInt(endParts[0], 10) + parseInt(endParts[1], 10) / 60;
                                                                // 如果结束时间是00:00（endHour=0），说明是跨午夜预约，应该处理到24点
                                                                const actualEndHour = endHour === 0 ? 24 : endHour;
                                                                if (actualEndHour > 12) {
                                                                    slots.push({ start: Math.max(startHour, 12), end: Math.min(actualEndHour, 24), timeSlot: time });
                                                                }
                                                            } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
                                                                const startParts = time.startTime.split(' ')[1].split(':');
                                                                const startHour = parseInt(startParts[0], 10) + parseInt(startParts[1], 10) / 60;
                                                                if (startHour < 24) {
                                                                    slots.push({ start: Math.max(startHour, 12), end: 24, timeSlot: time });
                                                                }
                                                            } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
                                                                const endParts = time.endTime.split(' ')[1].split(':');
                                                                const endHour = parseInt(endParts[0], 10) + parseInt(endParts[1], 10) / 60;
                                                                // 如果结束时间是00:00（endHour=0），说明是跨午夜预约，应该处理到24点
                                                                const actualEndHour = endHour === 0 ? 24 : endHour;
                                                                if (actualEndHour > 12) {
                                                                    slots.push({ start: 12, end: Math.min(actualEndHour, 24), timeSlot: time });
                                                                }
                                                            } else {
                                                                slots.push({ start: 12, end: 24, timeSlot: time });
                                                            }
                                                        }
                                                    }
                                                    return slots;
                                                };

                                                const reservationSlots = getReservationSlotsForDateLower();

                                                if (reservationSlots.length > 0) {
                                                    let currentTime = -1;
                                                    if (selectedDateOnly.getTime() === currentDate.getTime()) {
                                                        currentTime = now.getHours() + (now.getMinutes() / 60);
                                                    }

                                                    const mergedSlots = [];
                                                    let currentSlot = null;
                                                    const sortedSlots = [...reservationSlots].sort((a, b) => a.start - b.start);

                                                    for (const slot of sortedSlots) {
                                                        if (!currentSlot) {
                                                            currentSlot = { ...slot };
                                                        } else {
                                                            if (slot.start <= currentSlot.end) {
                                                                currentSlot.end = Math.max(currentSlot.end, slot.end);
                                                            } else {
                                                                mergedSlots.push(currentSlot);
                                                                currentSlot = { ...slot };
                                                            }
                                                        }
                                                    }
                                                    if (currentSlot) {
                                                        mergedSlots.push(currentSlot);
                                                    }

                                                    const finalSlots = [];
                                                    let lastEnd = 12;

                                                    for (const reservedSlot of mergedSlots) {
                                                        if (currentTime >= 0 && lastEnd < currentTime) {
                                                            if (currentTime < reservedSlot.start) {
                                                                // 当前时间在预约之前
                                                                if (currentTime > lastEnd) {
                                                                    finalSlots.push({
                                                                        start: lastEnd,
                                                                        end: currentTime,
                                                                        status: 'unavailable'
                                                                    });
                                                                    lastEnd = currentTime;
                                                                }
                                                            } else if (currentTime < reservedSlot.end) {
                                                                // 当前时间在预约内部
                                                                if (reservedSlot.start > lastEnd) {
                                                                    finalSlots.push({
                                                                        start: lastEnd,
                                                                        end: reservedSlot.start,
                                                                        status: 'unavailable'
                                                                    });
                                                                    lastEnd = reservedSlot.start;
                                                                }
                                                            } else {
                                                                // 当前时间在预约之后
                                                                if (reservedSlot.end > lastEnd) {
                                                                    finalSlots.push({
                                                                        start: lastEnd,
                                                                        end: reservedSlot.end,
                                                                        status: 'unavailable'
                                                                    });
                                                                    lastEnd = reservedSlot.end;
                                                                }
                                                                continue;
                                                            }
                                                        }

                                                        if (reservedSlot.start > lastEnd) {
                                                            finalSlots.push({
                                                                start: lastEnd,
                                                                end: reservedSlot.start,
                                                                status: 'available'
                                                            });
                                                            lastEnd = reservedSlot.start;
                                                        }

                                                        finalSlots.push({
                                                            start: reservedSlot.start,
                                                            end: reservedSlot.end,
                                                            status: 'reserved',
                                                            timeSlot: reservedSlot.timeSlot
                                                        });
                                                        lastEnd = reservedSlot.end;
                                                    }

                                                    if (lastEnd < 24) {
                                                        finalSlots.push({
                                                            start: lastEnd,
                                                            end: 24,
                                                            status: 'available'
                                                        });
                                                    }

                                                    const validSlots = finalSlots.filter(slot => slot.start < slot.end);

                                                    return validSlots.map((slot, index) => {
                                                        const left = `${((slot.start - 12) / 12) * 100}%`;
                                                        const width = `${((slot.end - slot.start) / 12) * 100}%`;

                                                        let backgroundColor;
                                                        switch (slot.status) {
                                                            case 'available':
                                                                backgroundColor = 'rgba(82, 196, 26, 0.6)';
                                                                break;
                                                            case 'reserved':
                                                                backgroundColor = '#FE9874';
                                                                break;
                                                            case 'unavailable':
                                                                backgroundColor = 'rgba(191, 191, 191, 0.8)';
                                                                break;
                                                            default:
                                                                backgroundColor = 'rgba(204, 204, 204, 0.3)';
                                                        }

                                                        return (
                                                            <div
                                                                key={index}
                                                                className={`time-slot-status ${slot.status}`}
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: left,
                                                                    width: ((slot.end - slot.start) / 12) * 100 >= 100 ? '100%' : `max(${((slot.end - slot.start) / 12) * 100}%, 2px)`,
                                                                    bottom: 0,
                                                                    backgroundColor: backgroundColor,
                                                                    pointerEvents: slot.status === 'reserved' ? 'auto' : 'none',
                                                                    cursor: slot.status === 'reserved' ? 'pointer' : undefined,
                                                                    zIndex: slot.status === 'reserved' ? 10 : undefined,
                                                                    borderRadius: '4px',
                                                                    padding: 0,
                                                                    margin: 0,
                                                                    boxSizing: 'border-box'
                                                                }}
                                                                title={slot.status === 'reserved' && slot.timeSlot ? `${formatTime(slot.timeSlot.startTime)} - ${formatTime(slot.timeSlot.endTime)}` : undefined}
                                                                {...(slot.status === 'reserved' && slot.timeSlot ? bindReservationDetailEvents(slot.timeSlot, `${vehicle.vehicleId}-lower-${index}`) : {})}
                                                            ></div>
                                                        );
                                                    });
                                                } else {
                                                    let currentTime = -1;
                                                    if (selectedDateOnly.getTime() === currentDate.getTime()) {
                                                        currentTime = now.getHours() + (now.getMinutes() / 60);
                                                    }

                                                    const validSlots = [];

                                                    if (currentTime >= 12) {
                                                        if (currentTime < 24) {
                                                            validSlots.push({
                                                                start: 12,
                                                                end: currentTime,
                                                                status: 'unavailable'
                                                            });
                                                            validSlots.push({
                                                                start: currentTime,
                                                                end: 24,
                                                                status: 'available'
                                                            });
                                                        } else {
                                                            validSlots.push({
                                                                start: 12,
                                                                end: 24,
                                                                status: 'unavailable'
                                                            });
                                                        }
                                                    } else {
                                                        validSlots.push({
                                                            start: 12,
                                                            end: 24,
                                                            status: 'available'
                                                        });
                                                    }

                                                    return validSlots.map((slot, index) => {
                                                        const left = `${((slot.start - 12) / 12) * 100}%`;
                                                        const width = `${((slot.end - slot.start) / 12) * 100}%`;

                                                        let backgroundColor;
                                                        switch (slot.status) {
                                                            case 'available':
                                                                backgroundColor = 'rgba(82, 196, 26, 0.6)';
                                                                break;
                                                            case 'reserved':
                                                                backgroundColor = '#FE9874';
                                                                break;
                                                            case 'unavailable':
                                                                backgroundColor = 'rgba(191, 191, 191, 0.8)';
                                                                break;
                                                            default:
                                                                backgroundColor = 'rgba(204, 204, 204, 0.3)';
                                                        }

                                                        return (
                                                            <div
                                                                key={index}
                                                                className={`time-slot-status ${slot.status}`}
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: left,
                                                                    width: ((slot.end - slot.start) / 12) * 100 >= 100 ? '100%' : `max(${((slot.end - slot.start) / 12) * 100}%, 2px)`,
                                                                    bottom: 0,
                                                                    backgroundColor: backgroundColor,
                                                                    pointerEvents: 'none',
                                                                    borderRadius: '4px',
                                                                    padding: 0,
                                                                    margin: 0,
                                                                    boxSizing: 'border-box'
                                                                }}
                                                            ></div>
                                                        );
                                                    });
                                                }
                                            })()}
                                        </div>
                                        <div className="time-label2 bottom">
                                            <span>12</span>
                                            <span>13</span>
                                            <span>14</span>
                                            <span>15</span>
                                            <span>16</span>
                                            <span>17</span>
                                            <span>18</span>
                                            <span>19</span>
                                            <span>20</span>
                                            <span>21</span>
                                            <span>22</span>
                                            <span>23</span>
                                            <span>24</span>
                                        </div>
                                    </div>
                                
                                {/* 时间状态摘要 */}
                                {(() => {
                                    const dateParts = selectedDate.split('-');
                                    const selectedDateOnly = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
                                    const now = new Date();
                                    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

                                    let currentTime = -1;
                                    if (selectedDateOnly.getTime() === currentDate.getTime()) {
                                        currentTime = now.getHours() + (now.getMinutes() / 60);
                                    }
                                    // ... existing code ...
                                    const vehicleTimesForSummary = vehicleUsageTimes
                                        .filter(item => {
                                            const isVehicleMatch = item.vehicleId && vehicle.vehicleId && item.vehicleId === vehicle.vehicleId;
                                            const startDateTime = new Date(item.startTime);
                                            const endDateTime = new Date(item.endTime);
                                            const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                                            const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
                                            const isDateInRange = selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
                                            return isVehicleMatch && isDateInRange;
                                        })
                                        .filter((item, index, self) => {
                                            const key = `${item.startTime}-${item.endTime}`;
                                            return self.findIndex(t => `${t.startTime}-${t.endTime}` === key) === index;
                                        });

                                    // 计算可预约时间段
                                    const mergedSlots = [];
                                    const sortedSlots = [...vehicleTimesForSummary].sort((a, b) => {
                                        const aStart = new Date(a.startTime);
                                        const bStart = new Date(b.startTime);
                                        return aStart - bStart;
                                    });

                                    for (const time of sortedSlots) {
                                        const startDateTime = new Date(time.startTime);
                                        const endDateTime = new Date(time.endTime);
                                        const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                                        const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());

                                        let startHour, endHour;
                                        if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
                                            startHour = startDateTime.getHours() + startDateTime.getMinutes() / 60;
                                            endHour = endDateTime.getHours() + endDateTime.getMinutes() / 60;
                                        } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
                                            startHour = startDateTime.getHours() + startDateTime.getMinutes() / 60;
                                            endHour = 24;
                                        } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
                                            startHour = 0;
                                            endHour = endDateTime.getHours() + endDateTime.getMinutes() / 60;
                                        } else {
                                            startHour = 0;
                                            endHour = 24;
                                        }

                                        if (mergedSlots.length === 0) {
                                            mergedSlots.push({ start: startHour, end: endHour });
                                        } else {
                                            const lastSlot = mergedSlots[mergedSlots.length - 1];
                                            if (startHour <= lastSlot.end) {
                                                lastSlot.end = Math.max(lastSlot.end, endHour);
                                            } else {
                                                mergedSlots.push({ start: startHour, end: endHour });
                                            }
                                        }
                                    }

                                    // 生成最终的时间段
                                    const finalSlots = [];
                                    let lastEnd = 0;

                                    for (const reservedSlot of mergedSlots) {
                                        if (currentTime >= 0 && lastEnd < currentTime) {
                                            const unavailableEnd = Math.min(reservedSlot.start, currentTime);
                                            if (unavailableEnd > lastEnd) {
                                                finalSlots.push({ start: lastEnd, end: unavailableEnd, status: 'unavailable' });
                                            }
                                            lastEnd = unavailableEnd;
                                        }

                                        if (reservedSlot.start > lastEnd) {
                                            finalSlots.push({ start: lastEnd, end: reservedSlot.start, status: 'available' });
                                        }

                                        const reservedStart = Math.max(reservedSlot.start, currentTime >= 0 ? currentTime : 0);
                                        if (reservedStart < reservedSlot.end) {
                                            finalSlots.push({ start: reservedStart, end: reservedSlot.end, status: 'reserved' });
                                        }

                                        lastEnd = reservedSlot.end;
                                    }

                                    if (lastEnd < 24) {
                                        if (currentTime >= 0 && lastEnd < currentTime) {
                                            finalSlots.push({ start: lastEnd, end: currentTime, status: 'unavailable' });
                                            lastEnd = currentTime;
                                        }
                                        if (lastEnd < 24) {
                                            finalSlots.push({ start: lastEnd, end: 24, status: 'available' });
                                        }
                                    }

                                    if (mergedSlots.length === 0) {
                                        if (currentTime >= 0) {
                                            finalSlots.push({ start: 0, end: currentTime, status: 'unavailable' });
                                            finalSlots.push({ start: currentTime, end: 24, status: 'available' });
                                        } else {
                                            finalSlots.push({ start: 0, end: 24, status: 'available' });
                                        }
                                    }

                                    // ... existing code ...
                                    // 格式化时间段显示
                                    const formatHour = (hour) => {
                                        const h = Math.floor(hour);
                                        const m = Math.round((hour - h) * 60);
                                        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                                    };

                                    // 最小时间间隔阈值（单位：小时），小于这个值的时间段不显示
                                    const MIN_TIME_THRESHOLD = 5 / 60; // 5分钟

                                    // 对finalSlots进行去重，避免相同时间段重复显示
                                    const uniqueSlots = [];
                                    const seenTimeRanges = new Set();

                                    finalSlots.forEach(slot => {
                                        // 使用起始时间和结束时间作为唯一标识
                                        const timeKey = `${slot.start}-${slot.end}`;
                                        if (!seenTimeRanges.has(timeKey)) {
                                            seenTimeRanges.add(timeKey);
                                            uniqueSlots.push(slot);
                                        }
                                    });

                                    // 过滤掉时间间隔过短的可预约时间段
                                    const availableSlots = uniqueSlots
                                        .filter(s => s.status === 'available')
                                        .filter(s => (s.end - s.start) >= MIN_TIME_THRESHOLD);

                                    if (availableSlots.length === 0) {
                                        return <div className="time-axis-summary no-available">🚫 今日无空闲时段</div>;
                                    }

                                    if (availableSlots.length === 1) {
                                        return (
                                            <div className="time-axis-summary available">
                                                ✅ 可预约：{formatHour(availableSlots[0].start)}-{formatHour(availableSlots[0].end)}
                                            </div>
                                        );
                                    }

                                    return (
                                        <div className="time-axis-summary">
                                            ✅ 可预约：{availableSlots.map((slot, idx) => (
                                            <span key={idx}>
                                                {formatHour(slot.start)}-{formatHour(slot.end)}
                                                {idx < availableSlots.length - 1 && '、'}
                                            </span>
                                        ))}
                                        </div>
                                    );

                                })()}
                            </div>
                        </div>
                        );
                    })
                )}
            </div>
            
            {/* 底部返回按钮 */}
            <div className="bottom-actions">
                <button className="back-to-reserve-btn" onClick={() => navigate('/')}>
                    返回首页
                </button>
            </div>

            {/* 预约详情弹窗 */}
            {showReservationDetail && (hoveredReservationData || selectedReservation) && (
                <div
                    className="reservation-detail-modal"
                    style={{
                        top: popupPosition.top,
                        left: popupPosition.left
                    }}
                    onMouseLeave={() => {
                        setHoveredReservationId(null);
                        setHoveredReservationData(null);
                        setShowReservationDetail(false);
                    }}
                >
                    <div className="reservation-detail-content">
                        <div className="reservation-detail-body">
                            <div className="reservation-detail-item">
                                <span className="label">预约时间：</span>
                                <span>{formatTime((hoveredReservationData || selectedReservation).startTime)} - {formatTime((hoveredReservationData || selectedReservation).endTime)}</span>
                            </div>
                            {(hoveredReservationData || selectedReservation).applicantName && (
                                <div className="reservation-detail-item">
                                    <span className="label">申请人：</span>
                                    <span>{(hoveredReservationData || selectedReservation).applicantName}</span>
                                </div>
                            )}
                            {(hoveredReservationData || selectedReservation).applicantDept && (
                                <div className="reservation-detail-item">
                                    <span className="label">部门：</span>
                                    <span>{(hoveredReservationData || selectedReservation).applicantDept}</span>
                                </div>
                            )}
                            {(hoveredReservationData || selectedReservation).applicantPhone && (
                                <div className="reservation-detail-item">
                                    <span className="label">联系电话：</span>
                                    <span>{(hoveredReservationData || selectedReservation).applicantPhone}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}